import * as React from 'react';
// mui
import {
  Box,
} from '@mui/material';
// redux
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { clearExcuse as clearTaskExcuse, setTasks } from '../../store/slices/tasksSlice';
import { clearExcuse as clearEventExcuse, setEvents } from '../../store/slices/eventsSlice';
import { clearExcuse as clearRecurringTaskExcuse, setRecurringTasks } from '../../store/slices/recurringTaskSlice';
import { selectSelectedProjectId } from '../../store/selectors/projectSelectors';
// types
import Task from '../../interfaces/Task.interface';
import Event from '../../interfaces/Event.interface';
import RecurringTask from '../../interfaces/RecurringTask.interface';
// axios
import api from '../../axios';
// components
import TaskList from '../../components/TasksPage/TaskList';
import TaskListSkeleton from '../../components/TasksPage/TasksListSkeleton';

export default function TasksPage() {
  const dispatch = useDispatch<AppDispatch>();

  const [ tasksReady, setTasksReady ] = React.useState<boolean>(false);
  const [ eventsReady, setEventsReady ] = React.useState<boolean>(false);
  const [ recurringTasksReady, setRecurringTasksReady ] = React.useState<boolean>(false);

  const localSelectedProjectId: number | null = useSelector(selectSelectedProjectId());
  const socketConnected = useSelector((state: RootState) => state.auth.socketConnected);
  const socketHasConnectedRef = React.useRef(false);

  const taskTimersRef = React.useRef<Record<number, NodeJS.Timeout>>({});
  const eventTimersRef = React.useRef<Record<number, NodeJS.Timeout>>({});
  const recurringTimersRef = React.useRef<Record<number, NodeJS.Timeout>>({});

  const setExcuseTimeout = (item: any, type: string) => {
    if (item.excused && item.excuseExpiresAt && !item.completed) {
      const expiresAt = new Date(item.excuseExpiresAt).getTime();
      const now = Date.now();
      const delay = expiresAt - now;

      if (delay >= 2147483647) {
        // setTimeout will not accept timeouts longer than this amount, so pass over this item
        return;
      }

      if (delay > 0) {
        if (type === 'task') {
          taskTimersRef.current[item.id] = setTimeout(() => {
            dispatch(clearTaskExcuse(item.id));
            delete taskTimersRef.current[item.id];
          }, delay);
        } else if (type === 'event') {
          eventTimersRef.current[item.id] = setTimeout(() => {
            dispatch(clearEventExcuse(item.id));
            delete eventTimersRef.current[item.id];
          }, delay);
        } else if (type === 'recurring') {
          recurringTimersRef.current[item.id] = setTimeout(() => {
            dispatch(clearRecurringTaskExcuse(item.id));
            delete recurringTimersRef.current[item.id];
          }, delay);
        }
      }
    }
  }

  const fetchTasks = async (projectId: number) => {
    setTasksReady(false);

    api.get(`/tasks/normalized/get/project/${projectId}`)
      .then((res) => {
        console.log(res);
        const fetchedTasks: Task[] = res.data;

        fetchedTasks.forEach((task: any) => {
          setExcuseTimeout(task, 'task');
        });

        dispatch(setTasks(fetchedTasks));
        setTasksReady(true);
      })
      .catch(console.log);
  };

  const fetchEvents = async (projectId: number) => {
    setEventsReady(false);

    api.get(`/events/get/project/${projectId}`)
      .then((res) => {
        console.log(res);
        const fetchedEvents: Event[] = res.data;

        fetchedEvents.forEach((event: any) => {
          setExcuseTimeout(event, 'event');
        });

        dispatch(setEvents(fetchedEvents));
        setEventsReady(true);
      })
      .catch(console.log);
  };

  const fetchRecurringTasks = async (projectId: number) => {
    setRecurringTasksReady(false);

    api.get(`/recurring/get/project/${projectId}`)
      .then((res) => {
        console.log(res);
        const fetchedRecurringTasks: RecurringTask[] = res.data;

        fetchedRecurringTasks.forEach((recurringTask: any) => {
          setExcuseTimeout(recurringTask, 'recurring');
        });

        dispatch(setRecurringTasks(fetchedRecurringTasks));
        setRecurringTasksReady(true);
      })
      .catch(console.log);
  };

  React.useEffect(() => {
    // when selected project ID changes OR socket reconnect
    if (localSelectedProjectId) {
      fetchTasks(localSelectedProjectId);
      fetchEvents(localSelectedProjectId);
      fetchRecurringTasks(localSelectedProjectId);
    }
  }, [localSelectedProjectId]);

  React.useEffect(() => {
    if (socketConnected) {
      if (socketHasConnectedRef.current) {
        // if a reconnection has occured fetch data
        if (localSelectedProjectId) {
          fetchTasks(localSelectedProjectId);
          fetchEvents(localSelectedProjectId);
          fetchRecurringTasks(localSelectedProjectId);
        }
      } else {
        // for first connection dont refresh
        socketHasConnectedRef.current = true;
      }
    }
  }, [socketConnected]);
  
  React.useEffect(() => {
    // when page unloaded
    return () => {
      Object.values(taskTimersRef.current).forEach((id) => clearTimeout(id));
      taskTimersRef.current = [];

      Object.values(eventTimersRef.current).forEach((id) => clearTimeout(id));
      eventTimersRef.current = [];

      Object.values(recurringTimersRef.current).forEach((id) => clearTimeout(id));
      recurringTimersRef.current = [];
    }
  }, []);

  return (
    <Box
      display="flex"
      justifyContent="center"
      minHeight="100%"
    >
      <Box sx={{ width: '100%', m: 0, background: '#121212' }}>
        { tasksReady && eventsReady && recurringTasksReady && socketConnected ?
          <TaskList
            parentTaskId={null}
            padding={-4}
          />
        : <TaskListSkeleton />}
      </Box>
    </Box>
  );
}
